// The blue "new" dots' data: GET /seen polled, and POST /seen when you look (the trail itself is in dots.ts).
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { NewSince, SeenKind } from '@dpw/shared';
import { api } from '../api';
import { dotsOf, withSeen } from './dots';

const KEY = ['seen'];

export function useNewSince() {
  const q = useQuery({ queryKey: KEY, queryFn: api.seen.get, refetchInterval: 60_000 });
  return useMemo(() => dotsOf(q.data), [q.data]);
}

/** Say you looked at something: the dot goes at once, and the server keeps it gone. */
export function useMarkSeen() {
  const qc = useQueryClient();
  return useCallback((kind: SeenKind, id: string | null = null) => {
    void qc.cancelQueries({ queryKey: KEY });
    qc.setQueryData<NewSince>(KEY, n => (n ? withSeen(n, kind, id) : n));
    api.seen.mark(kind, id).then(() => qc.invalidateQueries({ queryKey: KEY }), () => undefined);
  }, [qc]);
}

/** Pages that clear a dot by being open: the Knowledge base, the Research Center, a track's own page. */
export function useSeenOnVisit() {
  const { pathname } = useLocation();
  const dots = useNewSince();
  const mark = useMarkSeen();
  const track = /^\/tracks\/([0-9a-f-]{36})/.exec(pathname)?.[1] ?? null;
  const kb = pathname.startsWith('/folders') && dots.kb;
  const research = pathname.startsWith('/knowledge') && dots.research;
  const newTrack = !!track && dots.trackIsNew(track);
  useEffect(() => { if (kb) mark('kb'); }, [kb, mark]);
  useEffect(() => { if (research) mark('research'); }, [research, mark]);
  useEffect(() => { if (newTrack) mark('track', track); }, [newTrack, track, mark]);
}
